import { FC, useEffect, useState, useMemo } from 'react';
import { BootstrapInput, useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import { useMutation, useQuery } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { GridColumns, GridCellParams, DataGrid, GridSortDirection } from '@material-ui/data-grid';
import { Grid, Paper, IconButton, Checkbox, Select, InputLabel, Box, FormControl } from '@material-ui/core';
import { Search } from 'components/Search';
import PreviewIcon from '@material-ui/icons/RemoveRedEyeSharp';
import { observer } from 'mobx-react-lite';
import { orange } from '@material-ui/core/colors';
import { UserEditRequestForm } from 'components/Forms/UserEditRequestForm';
import { UserEditRequestView } from 'components/Forms/UserEditRequestView';
import { useHistory } from 'react-router-dom';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { capitalizeFirstLetter, toFormatDateTime } from 'utils';
import { getRowsPerPageOptions } from 'utils/pagination';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import Calender from './../../../assets/icons/EndUser/Calender.svg';
import EmailIcon from './../../../assets/icons/EndUser/Email.svg';
import { TabsEditRequestProps } from './types';
import { ListItemText, MenuItem } from '@mui/material';
import { useIsMobile } from 'hooks/useResponsive';
export const UserEditRequestTable: FC<TabsEditRequestProps> = observer(({ newRequest }) => {
  const classes = useStyles();
  const history = useHistory();
  const isMobile = useIsMobile();
  useEffect(() => {
    document.title = 'EasyIdentity: Requests from End Users';
  }, []);
  const {
    userEditRequestTableStore: {
      userEditRequestView,
      newuserEditRequestView,
      newUserRequestReadNotification,
      getUserEditRequest,
      setFilter,
      setSelectedId,
      pushId,
      getIds,
      viewedEndRequest,
      getNewUserEditRequest,
    },
    notificationStore: { openNotification },
    userStore: { getNewEndUserNotificationUpdate, setNewEndUserNotificationUpdate },
  } = useStore();

  const [openUserRequestModal, setOpenUserRequestModal] = useState<boolean>(false);
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);
  const [getId, setId] = useState<number | null>(null);
  const [filterState, setFilterState] = useState<string[]>([]);

  const getUserEditRequestReq = useQuery(
    ['getUserEditRequestReq', newRequest],
    () => (newRequest ? getNewUserEditRequest() : getUserEditRequest()),
    {
      onError: (e) => {
        openNotification('error', `Can't get users request`);
      },
    }
  );
  useEffect(() => {
    getUserEditRequestReq.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newRequest]);

  const updateGroupReq = useMutation((data: number) => viewedEndRequest(data), {
    mutationKey: 'viewedEndRequest',
    onError: () => openNotification('error', `Can't update end user view status `),
  });

  const newUserRequestReadNotificationReq = useMutation('newUserRequestReadNotification', (id: number) =>
    newUserRequestReadNotification(id)
  );
  const getRenderValueForRequestSelect = () => {
    const renderValues = filterState.map((group: any) => `${capitalizeFirstLetter(group)}`);
    return renderValues.join(', ');
  };

  useAppPreloader(
    newRequest
      ? getUserEditRequestReq.isLoading
      : userEditRequestView.length === 0
      ? getUserEditRequestReq.isFetching
      : getUserEditRequestReq.isLoading
  );
  // useAppPreloader( userEditRequestView.length === 0 && !newRequest ? getUserEditRequestReq.isFetching : getUserEditRequestReq.isLoading );

  const handleClickView = (id: number, status: string, reasonData: string): void => {
    if (newRequest) {
      newUserRequestReadNotificationReq.mutate(id);
    } else {
      updateGroupReq.mutate(id);
    }

    history.push(handleCompanyUrl(`/admin/users-updated-information/${id}`), {
      state: { newRequest: newRequest, rejected: status === 'rejected' ? true : false, reason: reasonData },
    });
  };
  const handleClose = () => {
    setSelectedId(0);
    setOpenUserRequestModal(false);
  };
  const handleCloseView = () => {
    setSelectedId(0);
    setOpenViewModal(false);
  };

  if (getNewEndUserNotificationUpdate === true) {
    getUserEditRequestReq.refetch();
    setNewEndUserNotificationUpdate(false);
  }
  const rows = useMemo(() => {
    const base = (newRequest ? newuserEditRequestView : userEditRequestView) as any[];
    if (filterState.length === 0) return base;
    return base.filter((row) => filterState.includes(row.status));
  }, [userEditRequestView, newuserEditRequestView, newRequest, filterState]);

  const [pageSize, setPageSize] = useState<number>(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);

  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      renderCell: (params: GridCellParams) => <RecordVoiceOverIcon style={{ color: 'grey' }} />,
    },
    {
      field: newRequest ? 'createdAt' : 'date',
      headerName: 'DATE/TIME',
      width: 220,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <div style={{ display: 'flex', gap: '10px' }}>
          <img src={Calender} alt='' />
          {toFormatDateTime(newRequest ? params.row.createdAt.toString() : params.row.date.toString())}
        </div>
      ),
    },
    {
      field: 'email',
      headerName: 'EMAIL ADDRESS',
      flex: 1,
      width: 220,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');
        return (
          <div style={{ display: 'flex', gap: '10px' }}>
            <img src={EmailIcon} alt='' />
            {value}
          </div>
        );
      },
    },
    { field: 'fullName', headerName: 'FULL NAME', width: 180, flex: 1 },
    {
      field: 'status',
      headerName: 'STATUS',
      flex: 0.5,
      renderCell: (params: GridCellParams) => (
        <p style={{ color: params.value === 'pending' ? '#FFD700' : params.value === 'approved' ? 'green' : 'red' }}>
          {capitalizeFirstLetter(params.row.status)}
        </p>
      ),
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      sortable: false,
      align: 'center',
      disableColumnMenu: true,
      width: 100,
      renderCell: (params: GridCellParams) => (
        <IconButton
          onClick={() => {
            setId(+params.row.id);
            if (params.row.status === 'pending') {
              pushId(+params.row.id);
            }
            handleClickView(+params.row.id, params.row.status, params.row.description);
          }}
          size='small'
        >
          <PreviewIcon style={{ color: orange[500] }} />
        </IconButton>
      ),
    },
  ];

  return (
    <div className={classes.container}>
      <h2 className={classes.textBox}>
        {newRequest ? 'New Account Update Requests from End-Users' : 'All Account Update Requests from All Users'}
      </h2>
      <Paper elevation={0}>
        <Grid container justify='space-between' alignItems='center' className={classes.box}>
          <Box style={{ minWidth: 120, marginLeft: '16px' }}>
            <FormControl fullWidth>
              <InputLabel id='demo-simple-select-label'>Select Filter</InputLabel>
              <Select
                input={<BootstrapInput />}
                className={classes.select}
                labelId='demo-simple-select-label'
                id='demo-simple-select'
                value={filterState}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                }}
                multiple
                // onChange={(event) => {
                //   const { value } = event.target as HTMLSelectElement;
                //   setFilterState(value as unknown as string[]);
                // }}
                renderValue={() => {
                  if (Array.isArray(filterState) && filterState.length === 0) {
                    return <div>Selected</div>;
                  } else {
                    return <div>{getRenderValueForRequestSelect()}</div>;
                  }
                }}
              >
                {['pending', 'rejected'].map((item) => (
                  <MenuItem key={item} value={item} style={{ background: 'none' }}>
                    <Checkbox
                      className={classes.checkbox}
                      checked={filterState.includes(item)}
                      onChange={() =>
                        setFilterState((prev: string[]) =>
                          prev.includes(item) ? prev.filter((val: string) => val !== item) : [...prev, item]
                        )
                      }
                    />
                    <ListItemText primary={capitalizeFirstLetter(item)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Search handelSearch={setFilter} />
          </Box>
        </Grid>
        <div
          style={{
            minWidth: isMobile ? 900 : '100%',
            overflowX: 'auto',
          }}
        >
          <DataGrid
            autoHeight
            className={classes.boxStatus}
            getRowClassName={(params) =>
              params.row.read === 1
                ? 'box--read'
                : params.row.read === 0
                ? 'box--unread'
                : getIds.includes(params.row.id)
                ? 'box--rejecting'
                : 'box--pending'
            }
            sortingOrder={['asc', 'desc']}
            sortModel={[
              {
                field: newRequest ? 'createdAt' : 'date',
                sort: 'desc' as GridSortDirection,
              },
            ]}
            rows={rows}
            columns={columns}
            pageSize={pageSize}
            rowsPerPageOptions={rowsPerPageOptions}
            pagination
            onPageChange={(params) => setPageSize(params.pageSize)}
          />
        </div>
      </Paper>
      {getId && (
        <UserEditRequestView
          open={openViewModal}
          refetch={() => getUserEditRequestReq.refetch()}
          handleClose={handleCloseView}
          id={getId}
          setId={setId}
        />
      )}
      {getId && (
        <UserEditRequestForm
          open={openUserRequestModal}
          label=''
          defaultValue=''
          refetch={() => getUserEditRequestReq.refetch()}
          handleClose={handleClose}
          id={getId}
          setId={setId}
          payload={undefined}
        />
      )}
    </div>
  );
});
